import { deployEnvironment, ENVIRONMENT_LABEL } from '@features/clients';
import { Button, FlagKeyPill, Modal, WriteError } from '@shared/ui';

import { useStartAllOperation } from '../hooks/use-release-operation';

import { confirmView } from './operation-confirm';
import { explainReleaseWriteFailure } from './release-error-copy';

import type { ReleaseComponent } from '../schemas/release-component';

/**
 * Confirm turning a release on for all clients. Mounted only while open. Turn-off
 * has its own dialog behind the typed phrase (`TurnOffReleaseDialog`).
 * The request answers 202 once the operation is queued; the page then shows its
 * status.
 */
export function TurnOnReleaseDialog({
    release,
    onClose,
}: {
    release: ReleaseComponent;
    onClose: () => void;
}) {
    const start = useStartAllOperation(release.id);
    const failure = start.error ? explainReleaseWriteFailure(start.error) : null;
    const view = confirmView({
        releaseName: release.name,
        enabled: true,
        flags: (release.members ?? []).map((member) => ({
            key: member.key,
            name: member.name,
            archived: member.archived,
        })),
        environment: ENVIRONMENT_LABEL[deployEnvironment()],
    });

    const submit = () => {
        start.mutate({ enabled: true }, { onSuccess: onClose });
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!start.isPending}
            title={view.title}
            description={view.overrideNotice}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={start.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={start.isPending}>
                        {start.isPending ? 'Starting…' : view.confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 text-[13px]">
                <p className="font-medium text-content-strong">{view.environmentLabel}</p>
                <div>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                        {view.summary}
                    </p>
                    <ul className="flex flex-col gap-1.5">
                        {view.flags.map((flag) => (
                            <li key={flag.key} className="flex items-center gap-2">
                                <FlagKeyPill>{flag.key}</FlagKeyPill>
                                {flag.note !== undefined && (
                                    <span className="text-xs text-content-muted">{flag.note}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
                {failure ? (
                    <div
                        role="alert"
                        className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                    >
                        <p className="font-semibold">{failure.title}</p>
                        <p className="mt-0.5 text-danger-muted">{failure.body}</p>
                        {failure.detail !== undefined && (
                            <p className="mt-1.5 text-danger-muted">{failure.detail}</p>
                        )}
                    </div>
                ) : (
                    start.error && <WriteError error={start.error} />
                )}
            </div>
        </Modal>
    );
}
